import { Order, OrderProduct, OrderCombination } from '../interfaces';
import { Seat, SeatMealPlanResult } from '../interfaces/mealPlan';

// Shared split-bill total helpers so the displayed per-seat amount and the amount actually
// charged (cash / card / charge-to-room) are computed identically and BOTH include the
// order-level discount. Previously each split path summed raw product prices and dropped the
// order discount, so split guests were charged the pre-discount total.

// Unit price of a line after its OWN per-line discount (mirrors the receipt's per-product /
// per-combination discount math). Does not include add-ons.
export const getLineNetPrice = (
    price: number,
    discount?: number | null,
    discountType?: string | null,
): number => {
    if (!discount || discount <= 0) {
        return price;
    }
    if (discountType === 'amount') {
        return price - discount;
    }
    if (discountType === 'percentage') {
        return price - (discount / 100) * price;
    }
    return price;
};

export const getSeatItemsTotal = (
    seatId: string,
    products: OrderProduct[],
    combinations: OrderCombination[],
): number => {
    const prodTotal = products
        .filter(p => p.seatId === seatId)
        .reduce((sum, p) => sum + (getLineNetPrice(p.price, p.discount, p.discountType) * p.qty), 0);
    const combTotal = combinations
        .filter(c => c.seatId === seatId)
        .reduce((sum, c) => sum + getLineNetPrice(c.price, c.discount, c.discountType), 0);
    return prodTotal + combTotal;
};

// What a seat is responsible for BEFORE the order-level discount: gross seat total with
// meal-plan-included items removed for hotel guests (matches the split view's prior logic).
export const getSeatChargeableBase = (
    seat: Seat,
    products: OrderProduct[],
    combinations: OrderCombination[],
    seatResults: Record<string, SeatMealPlanResult>,
): number => {
    const itemsTotal = getSeatItemsTotal(seat.id, products, combinations);
    const included = seatResults[seat.id]?.totalIncluded || 0;
    return seat.type === 'hotel_guest' ? itemsTotal - included : itemsTotal;
};

// This seat's share of the ORDER-LEVEL discount. A percentage discount applies uniformly to
// the seat's chargeable base; a fixed amount is allocated proportionally to each seat's
// chargeable share so the per-seat discounts sum to the whole-order discount.
export const getSeatOrderDiscount = (
    seat: Seat,
    order: Order | null | undefined,
    seats: Seat[],
    products: OrderProduct[],
    combinations: OrderCombination[],
    seatResults: Record<string, SeatMealPlanResult>,
): number => {
    const value = order?.orderDiscountValue || 0;
    if (!order || !value) {
        return 0;
    }

    const base = getSeatChargeableBase(seat, products, combinations, seatResults);
    if (base <= 0) {
        return 0;
    }

    if (order.orderDiscountType === 'percentage') {
        return (value / 100) * base;
    }
    // 'amount' (fixed currency) — allocate proportionally to chargeable share
    const totalBase = seats.reduce(
        (sum, s) => sum + getSeatChargeableBase(s, products, combinations, seatResults),
        0,
    );
    return totalBase > 0 ? value * (base / totalBase) : 0;
};

// Final amount a seat must pay: chargeable base minus its share of the order-level discount.
export const getSeatToPay = (
    seat: Seat,
    order: Order | null | undefined,
    seats: Seat[],
    products: OrderProduct[],
    combinations: OrderCombination[],
    seatResults: Record<string, SeatMealPlanResult>,
): number => {
    const base = getSeatChargeableBase(seat, products, combinations, seatResults);
    const discount = getSeatOrderDiscount(seat, order, seats, products, combinations, seatResults);
    return base - discount;
};
